import { LoadedProps, RouteLoadProps, defRoute } from '@components/router';
import { FixedContent, FixedPage } from '@course/components/fixed-page';
import { AdminNav, AdminTabHeader, AdminTabWrapper } from '@course/components/admin-nav';
import { rpx } from 'client/lib/rpx-client';
import { CommsAgenda } from '@components/comms-agenda';
import { ScheduleViewToggle, useScheduleView } from '@components/comms-agenda/view-toggle';
import { useMemo, useState } from 'preact/hooks';
import { showCoursePickerModal } from './course-picker-modal';
import { BtnPrimary } from '@components/buttons';
import { IcoPlus } from '@components/icons';
import { upcomingDateRange } from 'shared/comms-schedule';

async function load(_route: RouteLoadProps) {
  const { startDate, endDate } = upcomingDateRange();
  const [items, composableCourses] = await Promise.all([
    rpx.commsSchedule.getCommsSchedule({ startDate, endDate }),
    rpx.messages.getComposableCourses(),
  ]);
  return { items, composableCourses };
}

export const route = defRoute({
  load,
  authLevel: 'admin',
  Page,
});

function Page({ state }: LoadedProps<typeof load>) {
  const [courseFilter, setCourseFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<'all' | 'message' | 'reminder'>('all');
  const { view, setView, items, isLoading } = useScheduleView({
    initialItems: state.items,
    fetch: (range) => rpx.commsSchedule.getCommsSchedule(range),
  });

  const courses = useMemo(() => {
    const map = new Map<string, string>();
    for (const item of items) {
      map.set(item.courseId, item.courseTitle);
    }
    return Array.from(map.entries())
      .map(([id, title]) => ({ id, title }))
      .sort((a, b) => a.title.localeCompare(b.title));
  }, [items]);

  const filtered = useMemo(() => {
    return items.filter((item) => {
      if (courseFilter !== 'all' && item.courseId !== courseFilter) {
        return false;
      }
      if (typeFilter === 'message' && item.type !== 'message') {
        return false;
      }
      if (typeFilter === 'reminder' && item.type === 'message') {
        return false;
      }
      return true;
    });
  }, [items, courseFilter, typeFilter]);

  return (
    <FixedPage>
      <FixedContent class="bg-white">
        <AdminNav currentPage="emailSchedule" pageTitle="Email schedule" />
        <AdminTabWrapper>
          <AdminTabHeader title="Email schedule">
            {state.composableCourses.length > 0 && (
              <BtnPrimary
                onClick={() => showCoursePickerModal({ courses: state.composableCourses })}
              >
                <IcoPlus class="w-4 h-4" /> New message
              </BtnPrimary>
            )}
          </AdminTabHeader>
          <p class="text-sm text-gray-500 -mt-6 mb-6">
            Scheduled course messages and meeting reminders across every course in the tenant.
          </p>
          <div class="mb-4">
            <ScheduleViewToggle view={view} onChange={setView} />
          </div>
          <div class="flex flex-wrap gap-3 mb-6 items-center">
            <label class="flex items-center gap-2 text-sm">
              <span class="text-gray-700">Course</span>
              <select
                class="border border-gray-300 rounded px-2 py-1 text-sm bg-white"
                value={courseFilter}
                onChange={(e) => setCourseFilter((e.target as HTMLSelectElement).value)}
              >
                <option value="all">All courses</option>
                {courses.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.title}
                  </option>
                ))}
              </select>
            </label>
            <label class="flex items-center gap-2 text-sm">
              <span class="text-gray-700">Type</span>
              <select
                class="border border-gray-300 rounded px-2 py-1 text-sm bg-white"
                value={typeFilter}
                onChange={(e) =>
                  setTypeFilter((e.target as HTMLSelectElement).value as typeof typeFilter)
                }
              >
                <option value="all">All</option>
                <option value="message">Messages</option>
                <option value="reminder">Call reminders</option>
              </select>
            </label>
            <span class="text-xs text-gray-500 ml-auto">
              {filtered.length.toLocaleString()} send{filtered.length === 1 ? '' : 's'}
            </span>
          </div>
          <CommsAgenda
            items={filtered}
            showCourseColumn={true}
            view={view}
            emptyState={
              isLoading
                ? 'Loading…'
                : view === 'sent'
                  ? 'No comms sent in the past 6 months match these filters.'
                  : 'No scheduled comms match these filters.'
            }
          />
        </AdminTabWrapper>
      </FixedContent>
    </FixedPage>
  );
}
